import * as THREE from 'three';
import { Scene } from '../Core/Scene';
import { Sizes } from '../Core/Sizes';
import EventEmitter from '../Utils/EventEmitter';

/**
 * Base Section class
 * All specific sections will extend this base class
 */
export class Section extends EventEmitter {
  /**
   * Constructor
   * @param {Object} sectionData - Section data from SectionManager
   */
  constructor(sectionData) {
    super();
    this.sectionData = sectionData;
    this.scene = Scene.getInstance();
    this.sizes = Sizes.getInstance();
    // Create THREE.js group for this section
    this.group = new THREE.Group();
    this.group.name = `${this.constructor.name}-Group`;
    
    // Position the group at the section's world position
    this.group.position.y = this.sectionData.worldData.position.y;
    this.group.position.z = this.sectionData.worldData.position.z;

    // Add group to scene
    this.scene.add(this.group);

    // State
    this.opacity = 1.0;
    this.visible = null;
    this.initialized = false;
    this.progress = 0;

    // Initialize elements (to be implemented by subclasses)
    this.initialize();
    
    // Initial update
    this.update(0);
  }

  resize(){
    this.setBackgroundSize();
  }

  setBackgroundSize(){
    this.backgroundPlane.scale.set(
      this.sectionData.worldData.width,
      this.sectionData.worldData.height,
      1
    );
  }

  /**
   * Initialize section elements
   * This should be overridden by subclasses
   */
  initialize() {
    // Create a simple 1x1 plane geometry
    const planeGeometry = new THREE.PlaneGeometry(1, 1);

    // Parse color from section
    const color = new THREE.Color(this.sectionData.color);

    // Create material
    this.backgroundMaterial = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 1.0,
      side: THREE.FrontSide,
    });

    // Create mesh
    this.backgroundPlane = new THREE.Mesh(planeGeometry, this.backgroundMaterial);
    this.backgroundPlane.name = `${this.constructor.name}-BackgroundPlane`;
    this.backgroundPlane.frustumCulled = false;
    
    // Scale the mesh to match section dimensions
    this.backgroundPlane.scale.set(
      this.sectionData.worldData.width,
      this.sectionData.worldData.height,
      1
    );

    // Add to group
    this.group.add(this.backgroundPlane);
  }

  /**
   * Update section progress
   * @param {Number} progress - Progress value (0-1)
   */
  update(progress) {
    this.progress = progress;
    if (!this.initialized) {
      this.initialized = true;
    }
  }

  changeVisibility(visible) {
    if(this.visible !== visible) {
      this.visible = visible;
      this.group.visible = this.visible;
      this.trigger('visibilityChanged', [this.visible]);
    }
  }

  /**
   * Update section data
   * @param {Object} sectionData - New section data
   */
  updateSectionData(sectionData) {
    // Update section data
    this.sectionData = sectionData;
    
    // Update position
    this.group.position.y = this.sectionData.worldData.position.y;
    this.group.position.z = this.sectionData.worldData.position.z;

    this.resize();
  }

  /**
   * Get section data
   * @returns {Object} - Section data
   */
  getSectionData() {
    return this.sectionData;
  }

  /**
   * Dispose resources
   */
  dispose() {
    // Remove from scene
    this.scene.remove(this.group);

    // Dispose elements (to be implemented by subclasses)
    this.disposeElements();
    
    // Clear references
    this.group = null;
    this.sectionData = null;
  }

  /**
   * Dispose section elements
   * This should be overridden by subclasses
   */
  disposeElements() {
    // Dispose background
    if (this.backgroundPlane) {
      this.backgroundPlane.geometry.dispose();
      this.backgroundMaterial.dispose();
      this.backgroundPlane = null;
      this.backgroundMaterial = null;
    }
  }
} 